import { useState, type MouseEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { courseWorkspacePath, type CourseWorkspaceShell } from "@/navigation/routes";
import { readTrail, searchWithTrail } from "@/lib/visit/address";
import { groupsOriginState, groupsReturnState, historyIndex } from "./groups-origin";
import type { GroupsTrip } from "./use-coursework-settings";

/** This visit's address made exact: a Create that published becomes its work. */
function exactAddress(pathname: string, search: string, hash: string, publishedId: string | null): string {
  const visits = readTrail(search);
  const published = publishedId && visits.length === 1 && /^work:[^,/]+\/create$/.test(visits[0]);
  return `${pathname}${published ? searchWithTrail(search, [`work:${publishedId}`]) : search}${hash}`;
}

/**
 * Groups are People's (#1393 R2). The trip waits for the writing to settle,
 * a refused, failed or unanswered save keeps the teacher on its recovery;
 * then this entry becomes the exact work's address and People opens with an
 * origin naming it, so Return steps Back to it. The work's group revision
 * stays until the deliberate refresh.
 */
export function ManageGroups({ course, title, trip }: { course: string; title: string; trip: GroupsTrip }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<"idle" | "saving" | "held">("idle");
  const shell: CourseWorkspaceShell = location.pathname.startsWith("/admin/teaching") ? "adminTeaching" : "instructor";
  const to = `${courseWorkspacePath(shell, course, "people")}&part=groups`;
  const go = async (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    if (state === "saving") return;
    setState("saving");
    if (!await trip.leave()) { setState("held"); return; }
    // BrowserRouter writes history synchronously: the replaced entry keeps its
    // index, and People is pushed directly after it.
    const href = exactAddress(location.pathname, location.search, location.hash, trip.publishedId);
    const idx = historyIndex();
    navigate(href, { replace: true, state: groupsReturnState(location.state) });
    navigate(to, { state: groupsOriginState({ courseId: course, href, title, ...(idx === undefined ? {} : { idx }) }) });
  };
  return <>
    <Link data-manage-groups="" aria-disabled={state === "saving" || undefined} className="w-fit font-semibold text-primary" to={to} onClick={event => { void go(event); }}>Manage groups</Link>
    {state === "held" && <p role="status">Your writing is not saved yet, so groups stay closed. Resolve the save above, then manage groups.</p>}
  </>;
}
